import React, { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSimulador } from '../context/SimuladorContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import axios from 'axios';
import { 
  LayoutDashboard, 
  Package, 
  Users, 
  Settings, 
  LogOut, 
  BarChart3,
  AlertTriangle,
  DollarSign,
  CalendarDays,
  Eye,
  Building2,
  TrendingUp,
  TrendingDown,
  Search,
  Command,
  Tv,
  Layers,
  Factory,
  RefreshCw,
  Shield,
  Bell,
  Wrench,
  X,
  Wifi,
  WifiOff,
  AlertCircle,
  Clock,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import BuscaGlobal from './BuscaGlobal';
import SimuladorFlutuante from './SimuladorFlutuante';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from './ui/dialog';

const SAMIDEL_LOGO = "https://customer-assets.emergentagent.com/job_fabriflow/artifacts/vi8gu83e_logo-hero-samidel.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mapeamento de IDs de menus para configurações
const menuConfig = {
  'dashboard': { icon: LayoutDashboard, label: 'Dashboard', path: '/gestor/dashboard' },
  'planeamento': { icon: CalendarDays, label: 'Planeamento', path: '/gestor/planeamento' },
  'bolsa-tarefas': { icon: Layers, label: 'Tarefas Pontuais', path: '/gestor/bolsa-tarefas' },
  'ofs': { icon: Package, label: 'Ordens de Fabrico', path: '/gestor/ofs' },
  'anomalias': { icon: AlertTriangle, label: 'Anomalias', path: '/gestor/anomalias' },
  'alertas': { icon: Bell, label: 'Alertas', path: '/gestor/alertas' },
  'sistemas': { icon: Settings, label: 'Sistemas', path: '/gestor/sistemas' },
  'relatorios': { icon: BarChart3, label: 'Relatórios', path: '/gestor/relatorios' },
  'financeiro': { icon: DollarSign, label: 'Financeiro', path: '/gestor/financeiro' },
  'estado-operadores': { icon: Eye, label: 'Estado Operadores', path: '/gestor/estado-operadores' },
  'tv': { icon: Tv, label: 'TV Dashboard', path: '/tv' },
  'operadores': { icon: Users, label: 'Operadores', path: '/gestor/operadores' },
  'marcas': { icon: Building2, label: 'Marcas', path: '/gestor/marcas' },
  'operacoes': { icon: Layers, label: 'Operações', path: '/gestor/operacoes' },
  'configuracoes': { icon: Settings, label: 'Configurações', path: '/gestor/configuracoes' },
  'openai': { icon: Sparkles, label: 'OpenAI / ChatGPT', path: '/gestor/openai' },
};

// Menus principais (ordem fixa) — sem alertas/regras/sistema que vão para baixo
const menuItemsOrder = ['dashboard', 'planeamento', 'ofs', 'anomalias', 'relatorios', 'financeiro', 'estado-operadores', 'tv'];
// Menus inferiores (ordem fixa) — regras e sistema no fim
const menuItemsBottomOrder = ['operadores', 'marcas', 'operacoes', 'utilizadores', 'configuracoes', 'openai', 'alertas', 'sistemas'];

// ──────────────────────────────────────────────────────────────────────────────
// Layout persistente do gestor
// Antes: cada página envolvia-se no seu próprio <GestorLayout>, por isso a cada
// navegação o layout inteiro (sidebar, barra de topo, alertas, WebSocket de
// alertas, permissões) era desmontado e montado de novo — a app parecia estar
// sempre a "atualizar" (menu a piscar, 5-6 pedidos repetidos por navegação).
// Agora o App.js monta UM <GestorShell> (rota-pai com <Outlet/>) e o
// <GestorLayout> usado dentro das páginas passa a ser apenas um pass-through.
// ──────────────────────────────────────────────────────────────────────────────
const GestorShellContext = createContext(false);

// Cache de permissões do gestor entre montagens (evita o menu "piscar")
let _permissoesCache = null;

export function GestorShell() {
  const location = useLocation();
  // Ao mudar de página, voltar ao topo (antes acontecia por o layout ser remontado)
  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);
  return (
    <GestorShellContext.Provider value={true}>
      <GestorLayoutInner>
        <Outlet />
      </GestorLayoutInner>
    </GestorShellContext.Provider>
  );
}

export default function GestorLayout({ children }) {
  const dentroDoShell = useContext(GestorShellContext);
  if (dentroDoShell) return <>{children}</>;
  return <GestorLayoutInner>{children}</GestorLayoutInner>;
}

function GestorLayoutInner({ children }) {
  const { logout, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showBusca, setShowBusca] = useState(false);

  // --- Alertas unificados state ---
  const [showAlertasPopover, setShowAlertasPopover] = useState(false);
  const [alertasChamadas, setAlertasChamadas] = useState([]);
  const [alertasContagem, setAlertasContagem] = useState([]);
  const [alertasInatividade, setAlertasInatividade] = useState([]);
  const [alertasAnomalias, setAlertasAnomalias] = useState([]);
  const [alertasBlink, setAlertasBlink] = useState(false);
  const prevAlertCountRef = useRef(-1);
  const prevChamadasCountRef = useRef(-1);
  const popoverRef = useRef(null);
  const [alertaModal, setAlertaModal] = useState(null); // Modal centrado para novo pedido de ajuda
  const alertSoundRef = useRef(null);
  const [prefsAlertasAtivos, setPrefsAlertasAtivos] = useState(['chamadas', 'anomalia', 'inatividade', 'contagem']);
  
  // WebSocket para notificações real-time
  const { connected, mode, lastNotification, unreadCount, resetUnread } = useWebSocket();

  // Criar referência do áudio para alerta sonoro
  useEffect(() => {
    // Usar AudioContext para gerar som de alarme programaticamente
    alertSoundRef.current = {
      play: () => {
        try {
          const ctx = new (window.AudioContext || window.webkitAudioContext)();
          // Tocar 3 beeps urgentes
          [0, 0.3, 0.6].forEach(delay => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.frequency.value = 880;
            osc.type = 'square';
            gain.gain.value = 0.15;
            osc.start(ctx.currentTime + delay);
            osc.stop(ctx.currentTime + delay + 0.2);
          });
        } catch { /* browser blocked audio */ }
      }
    };
  }, []);

  // Refs para que fetchAlertas seja estável (antes era recriado a cada 30s quando as
  // preferências eram recarregadas → o polling reiniciava e disparava pedidos extra)
  const prefsRef = useRef(prefsAlertasAtivos);
  const popoverAbertoRef = useRef(showAlertasPopover);
  useEffect(() => { prefsRef.current = prefsAlertasAtivos; }, [prefsAlertasAtivos]);
  useEffect(() => { popoverAbertoRef.current = showAlertasPopover; }, [showAlertasPopover]);

  const fetchAlertas = useCallback(async () => {
    try {
      const [chamRes, contRes, inatRes, anomRes] = await Promise.all([
        axios.get(`${API}/alertas-gestor/chamadas`).catch(() => ({ data: [] })),
        axios.get(`${API}/alertas-contagem?resolvido=false`).catch(() => ({ data: [] })),
        axios.get(`${API}/alertas-inatividade?resolvido=false`).catch(() => ({ data: [] })),
        axios.get(`${API}/alertas-anomalias?resolvido=false`).catch(() => ({ data: [] })),
      ]);
      const chamadas = (chamRes.data || []).filter(a => !a.resolvido);
      const contagem = contRes.data || [];
      const inatividade = inatRes.data || [];
      const anomalias = anomRes.data || [];
      setAlertasChamadas(chamadas);
      setAlertasContagem(contagem);
      setAlertasInatividade(inatividade);
      setAlertasAnomalias(anomalias);

      const totalNow = chamadas.length + contagem.length + inatividade.length + anomalias.length;
      // Modal "Pedido de Ajuda" só aparece quando há NOVA chamada RECENTE (< 60s)
      if (chamadas.length > prevChamadasCountRef.current && prevChamadasCountRef.current >= 0) {
        const novoAlerta = chamadas[0];
        // Verificar se a chamada é recente (criada nos últimos 60 segundos)
        const alertaRecente = novoAlerta?.criado_em && (Date.now() - new Date(novoAlerta.criado_em).getTime()) < 60000;
        if (novoAlerta && alertaRecente && prefsRef.current.includes('chamadas') && !popoverAbertoRef.current) {
          setAlertaModal(novoAlerta);
          alertSoundRef.current?.play();
        }
      }
      prevChamadasCountRef.current = chamadas.length;
      if (totalNow > prevAlertCountRef.current && prevAlertCountRef.current >= 0) {
        setAlertasBlink(true);
        setTimeout(() => setAlertasBlink(false), 3000);
      }
      prevAlertCountRef.current = totalNow;
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchAlertas();
    // Não fazer polling com a aba em segundo plano; ao voltar, actualiza logo
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') fetchAlertas();
    }, 10000);
    const onVisible = () => { if (document.visibilityState === 'visible') fetchAlertas(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', onVisible); };
  }, [fetchAlertas]);

  // WebSocket: re-fetch alertas quando chega uma notificação nova
  useEffect(() => {
    if (lastNotification) {
      fetchAlertas();
    }
  }, [lastNotification, fetchAlertas]);

  // Reset unread quando abre o Dialog de alertas
  useEffect(() => {
    if (showAlertasPopover) resetUnread();
  }, [showAlertasPopover, resetUnread]);

  // Carregar preferências de alertas
  useEffect(() => {
    axios.get(`${API}/preferencias-alertas`)
      .then(res => setPrefsAlertasAtivos(res.data?.tipos_ativos || ['chamadas', 'anomalia', 'inatividade', 'contagem']))
      .catch(() => {});
    const interval = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      axios.get(`${API}/preferencias-alertas`)
        .then(res => {
          const novos = res.data?.tipos_ativos || ['chamadas', 'anomalia', 'inatividade', 'contagem'];
          // Só actualizar o estado se mudou (evita re-render do layout a cada 30s)
          setPrefsAlertasAtivos(prev => (JSON.stringify(prev) === JSON.stringify(novos) ? prev : novos));
        })
        .catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // O Dialog gere a sua própria abertura/fecho

  const totalAlertas = alertasChamadas.length + alertasContagem.length + alertasInatividade.length + alertasAnomalias.length;
  const { abrirSimulador, operadoresAtivos, isAberto } = useSimulador();
  const [gestorPermissoes, setGestorPermissoes] = useState(_permissoesCache);

  // Verificar se o utilizador é admin
  const isAdmin = user?.role === 'admin';

  // Carregar permissões do gestor
  useEffect(() => {
    if (!isAdmin) {
      axios.get(`${API}/configuracoes/gestor-permissoes`)
        .then(res => { _permissoesCache = res.data.menus; setGestorPermissoes(res.data.menus); })
        .catch(() => setGestorPermissoes(_permissoesCache));
    }
  }, [isAdmin]);

  // Filtrar menus baseado no role e permissões
  const filteredMenuItems = menuItemsOrder
    .filter(id => {
      if (isAdmin) return true;
      if (gestorPermissoes === null) return true; // Enquanto carrega, mostrar todos
      return gestorPermissoes.includes(id);
    })
    .map(id => ({ id, ...menuConfig[id] }));

  const filteredMenuItemsBottom = menuItemsBottomOrder
    .filter(id => {
      if (id === 'utilizadores') return isAdmin; // Utilizadores é sempre só para admin
      if (id === 'sistemas') return isAdmin; // Sistemas é sempre só para admin
      if (id === 'openai') return isAdmin; // OpenAI/ChatGPT config é sempre só para admin
      if (isAdmin) return true;
      if (gestorPermissoes === null) return true;
      return gestorPermissoes.includes(id);
    })
    .map(id => {
      if (id === 'utilizadores') {
        return { id, icon: Shield, label: 'Utilizadores', path: '/gestor/utilizadores' };
      }
      return { id, ...menuConfig[id] };
    });

  // Atalho de teclado para abrir busca (Cmd+K ou Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowBusca(true);
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleLogout = () => {
    logout();
    toast.success('Sessão terminada');
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-warm-bg flex">
      {/* Sidebar - Fixed */}
      <aside className="w-64 bg-[#1a1a2e] text-white flex flex-col fixed h-screen z-30">
        {/* Logo */}
        <div className="p-4 border-b border-white/10">
          <Link to="/gestor/dashboard" className="flex items-center justify-center">
            <img 
              src={SAMIDEL_LOGO} 
              alt="Samidel" 
              className="h-10"
            />
          </Link>
        </div>

        {/* Menu Principal */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {filteredMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                  isActive 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`}
                data-testid={`menu-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <Icon className="h-5 w-5" />
                <span className="font-medium text-sm">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Menu Inferior - Operadores e Marcas */}
        <div className="border-t border-white/10 p-4 space-y-1">
          <p className="text-xs text-white/40 uppercase tracking-wider mb-2 px-3">Registos</p>
          {filteredMenuItemsBottom.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                  isActive 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`}
                data-testid={`menu-${item.label.toLowerCase()}`}
              >
                <Icon className="h-5 w-5" />
                <span className="font-medium text-sm">{item.label}</span>
              </Link>
            );
          })}
        </div>

        {/* Footer Compacto */}
        <div className="p-2 border-t border-white/10">
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-2 py-1.5 rounded-lg transition-colors text-white/70 hover:bg-red-500/20 hover:text-red-300 w-full"
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4" />
            <span className="font-medium text-xs">Terminar Sessão</span>
          </button>
        </div>
      </aside>

      {/* Main Content - With left margin for fixed sidebar */}
      <main className="flex-1 ml-64 min-h-screen overflow-y-auto">
        {/* Top Bar com Busca - Compacto */}
        <div className="sticky top-0 z-20 bg-warm-bg/95 backdrop-blur-sm border-b border-stone-200/50 px-8 py-2">
          <div className="flex items-center justify-between">
            {/* Botão de Busca Global */}
            <button
              onClick={() => setShowBusca(true)}
              className="flex items-center gap-3 px-4 py-2.5 bg-white rounded-full border border-stone-200 hover:border-stone-300 hover:shadow-sm transition-all w-96 text-left group"
              data-testid="busca-global-trigger"
            >
              <Search className="h-4 w-4 text-stone-400" />
              <span className="text-stone-400 flex-1">Pesquisar OFs, operadores, clientes...</span>
              <div className="flex items-center gap-1 px-1.5 py-0.5 bg-stone-100 rounded text-[10px] text-stone-500 group-hover:bg-stone-200 transition-colors">
                <Command className="h-3 w-3" />
                <span>K</span>
              </div>
            </button>
            
            <div className="flex items-center gap-3">
              {/* Botão Alertas Unificado (com WebSocket real-time) */}
              <div className="relative">
                <button
                  onClick={() => setShowAlertasPopover(!showAlertasPopover)}
                  className={`relative flex items-center gap-2 px-4 py-2.5 rounded-full transition-all font-semibold ${
                    totalAlertas > 0
                      ? 'bg-red-500 text-white hover:bg-red-600 shadow-md'
                      : 'bg-white border border-stone-200 text-stone-700 hover:border-red-300 hover:text-red-600'
                  } ${alertasBlink ? 'animate-pulse' : ''}`}
                  data-testid="btn-alertas-header"
                >
                  <Bell className="h-4 w-4" />
                  <span className="text-sm">Alertas</span>
                  {totalAlertas > 0 && (
                    <span className="ml-1 min-w-[20px] h-5 flex items-center justify-center rounded-full bg-white text-red-600 text-xs font-bold px-1">
                      {totalAlertas}
                    </span>
                  )}
                  {/* Indicador WebSocket */}
                  <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${connected ? 'bg-green-400' : mode === 'connecting' ? 'bg-amber-400 animate-pulse' : 'bg-stone-300'}`} title={mode === 'ws' ? 'Tempo real ativo' : mode === 'polling' ? 'Actualização automática' : 'A ligar...'} />
                </button>

                {/* Dialog de Alertas unificado */}
                <Dialog open={showAlertasPopover} onOpenChange={setShowAlertasPopover}>
                  <DialogContent className="max-w-lg rounded-2xl border-0 shadow-2xl p-0 overflow-hidden" data-testid="alertas-modal">
                    <DialogHeader className="px-6 py-4 bg-stone-50 border-b">
                      <div className="flex items-center justify-between pr-8">
                        <div className="flex items-center gap-2">
                          <DialogTitle className="font-bold text-lg text-stone-800">Alertas</DialogTitle>
                          {mode === 'ws' ? (
                            <span className="flex items-center gap-1 text-[10px] text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full">
                              <Wifi className="h-3 w-3" /> Live
                            </span>
                          ) : mode === 'polling' ? (
                            <span className="flex items-center gap-1 text-[10px] text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded-full">
                              <RefreshCw className="h-3 w-3" /> Auto
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-[10px] text-amber-500 bg-amber-50 px-1.5 py-0.5 rounded-full animate-pulse">
                              <Wifi className="h-3 w-3" /> A ligar...
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => { setShowAlertasPopover(false); navigate('/gestor/alertas'); }}
                          className="text-sm text-blue-600 hover:underline font-medium whitespace-nowrap"
                          data-testid="ver-todos-alertas"
                        >
                          Ver todos
                        </button>
                      </div>
                    </DialogHeader>
                    <div className="max-h-[60vh] overflow-y-auto divide-y divide-stone-100">
                      {totalAlertas === 0 && (
                        <div className="px-6 py-12 text-center text-stone-400 text-sm">Sem alertas por resolver</div>
                      )}
                      {/* Lista unificada ordenada por data */}
                      {[
                        ...alertasChamadas.map(a => ({ ...a, _tipo: 'chamada', _date: a.data || a.criado_em || '' })),
                        ...alertasContagem.map(a => ({ ...a, _tipo: 'contagem', _date: a.criado_em || '' })),
                        ...alertasInatividade.map(a => ({ ...a, _tipo: 'inatividade', _date: a.data_hora || a.criado_em || '' })),
                        ...alertasAnomalias.map(a => ({ ...a, _tipo: 'anomalia', _date: a.data_hora || a.criado_em || '' })),
                      ]
                        .sort((a, b) => (b._date || '').localeCompare(a._date || ''))
                        .map(a => {
                          if (a._tipo === 'chamada') {
                            return (
                              <div key={a.id} className="px-6 py-4 hover:bg-red-50/50 cursor-pointer" onClick={() => { setShowAlertasPopover(false); navigate('/gestor/alertas'); }}>
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                                    <Bell className="h-5 w-5 text-red-500" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-red-100 text-red-600">Ajuda</span>
                                    </div>
                                    <p className="text-base font-bold text-stone-800">{a.operador_nome || 'Operador'}</p>
                                    <p className="text-sm text-stone-500">
                                      {a.of_numero ? `OF ${a.of_numero}` : ''}
                                      {a.marca ? ` · ${a.marca}` : ''}
                                      {a.lote_codigo ? ` · ${a.lote_codigo}` : ''}
                                      {!a.of_numero && !a.marca && !a.lote_codigo ? 'Pedido de Ajuda' : ''}
                                    </p>
                                    <p className="text-xs text-stone-400 mt-0.5">{a._date ? new Date(a._date).toLocaleString('pt-PT', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' }) : ''}</p>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          if (a._tipo === 'contagem') {
                            return (
                              <div key={a.id} className="px-6 py-4 hover:bg-amber-50/30 cursor-pointer" onClick={() => { setShowAlertasPopover(false); navigate('/gestor/alertas'); }}>
                                <div className="flex items-start gap-3">
                                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${a.diferenca < 0 ? 'bg-red-100' : 'bg-emerald-100'}`}>
                                    {a.diferenca < 0 ? <TrendingDown className="h-5 w-5 text-red-600" /> : <TrendingUp className="h-5 w-5 text-emerald-600" />}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Contagem</span>
                                      <span className={`inline-flex items-center gap-1 text-sm font-black px-2 py-0.5 rounded-lg ${
                                        a.diferenca < 0 ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'
                                      }`}>
                                        {a.diferenca > 0 ? '+' : ''}{a.diferenca}
                                      </span>
                                    </div>
                                    <p className="text-sm font-semibold text-stone-800">{a.operacao_nome || 'Operação'}</p>
                                    <div className="flex items-center gap-3 mt-1 text-xs text-stone-500">
                                      <span>Esperado: <strong className="text-stone-700">{a.quantidade_esperada}</strong></span>
                                      <span>Contado: <strong className={a.diferenca < 0 ? 'text-red-600' : 'text-emerald-600'}>{a.quantidade_contada}</strong></span>
                                    </div>
                                    <p className="text-xs text-stone-400 mt-1">{a.operador_nome || ''} — {a._date ? new Date(a._date).toLocaleString('pt-PT', { hour: '2-digit', minute: '2-digit' }) : ''}</p>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          if (a._tipo === 'inatividade') {
                            return (
                              <div key={a.id} className="px-6 py-4 hover:bg-orange-50/30 cursor-pointer" onClick={() => { setShowAlertasPopover(false); navigate('/gestor/alertas'); }}>
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                                    <Clock className="h-5 w-5 text-orange-600" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">Inatividade</span>
                                    </div>
                                    <p className="text-sm font-semibold text-stone-800">{a.operador_nome || 'Operador'}</p>
                                    <p className="text-xs text-stone-500">{a.operacao_nome || ''} {a.of_numero ? `(OF ${a.of_numero})` : ''}</p>
                                    <p className="text-xs text-stone-400 mt-0.5">{a._date ? new Date(a._date).toLocaleString('pt-PT', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' }) : ''}</p>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          if (a._tipo === 'anomalia') {
                            return (
                              <div key={a.id} className="px-6 py-4 hover:bg-red-50/30 cursor-pointer" onClick={() => { setShowAlertasPopover(false); navigate('/gestor/alertas'); }}>
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                                    <AlertCircle className="h-5 w-5 text-red-600" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-red-100 text-red-600">Anomalia</span>
                                    </div>
                                    <p className="text-sm font-semibold text-stone-800">{a.operador_nome || 'Operador'}</p>
                                    <p className="text-xs text-stone-500">{a.descricao || `Quantidade: ${a.quantidade_anomalias || 0} anomalias`} {a.of_numero ? `(OF ${a.of_numero})` : ''}</p>
                                    <p className="text-xs text-stone-400 mt-0.5">{a._date ? new Date(a._date).toLocaleString('pt-PT', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' }) : ''}</p>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })}
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              
              {/* Botão de Produção no Cabeçalho */}
              <button
                onClick={() => navigate('/gestor/producao')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full transition-all ${
                  location.pathname === '/gestor/producao'
                    ? 'bg-primary text-white'
                    : 'bg-white border border-stone-200 text-stone-700 hover:border-primary hover:text-primary'
                }`}
                data-testid="btn-producao-header"
              >
                <Factory className="h-4 w-4" />
                <span className="font-medium text-sm">Produção</span>
              </button>
            </div>
          </div>
        </div>
        
        <div className="px-8 pt-4 pb-8">
          {children}
        </div>
      </main>
      
      {/* Botão Flutuante - Simulador de Operadores - Removido temporariamente */}
      {/* 
      <button
        onClick={abrirSimulador}
        className="fixed bottom-20 right-6 z-50 flex items-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all group"
        data-testid="simulador-fab"
        title="Simulador de Operadores"
      >
        <Tv className="h-5 w-5" />
        <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 whitespace-nowrap">
          Simulador {operadoresAtivos.length > 0 && `(${operadoresAtivos.length})`}
        </span>
        {operadoresAtivos.length > 0 && !isAberto && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {operadoresAtivos.length}
          </span>
        )}
      </button>
      */}
      
      {/* Simulador Flutuante - Removido temporariamente a pedido do utilizador */}
      {/* <SimuladorFlutuante /> */}
      
      {/* Modal de Busca Global */}
      <BuscaGlobal isOpen={showBusca} onClose={() => setShowBusca(false)} />
      
      {/* Modal de Alerta — Pedido de Ajuda (centrado no ecrã) */}
      {alertaModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setAlertaModal(null)}>
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-in zoom-in-95 duration-200"
            onClick={e => e.stopPropagation()}
            data-testid="alerta-modal-chamada"
          >
            {/* Header vermelho */}
            <div className="bg-red-600 px-6 py-4 flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center animate-pulse">
                <Bell className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">Pedido de Ajuda</h3>
                <p className="text-red-100 text-sm">{new Date(alertaModal.criado_em).toLocaleString('pt-PT', { hour: '2-digit', minute: '2-digit' })}</p>
              </div>
            </div>
            {/* Corpo */}
            <div className="px-6 py-5 space-y-4">
              {/* Nome do operador */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center">
                  <Users className="h-5 w-5 text-stone-600" />
                </div>
                <div>
                  <p className="text-xs text-stone-500 uppercase tracking-wider font-bold">Operador</p>
                  <p className="text-lg font-bold text-stone-800">{alertaModal.operador_nome || '—'}</p>
                </div>
              </div>
              {/* Info de produção ativa */}
              {(alertaModal.of_numero || alertaModal.marca || alertaModal.lote_codigo || alertaModal.operacao_nome) && (
                <div className="bg-stone-50 rounded-xl p-4 space-y-2.5 border border-stone-200">
                  <p className="text-xs text-stone-500 uppercase tracking-wider font-bold mb-2">Produção em Curso</p>
                  {alertaModal.of_numero && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-stone-500">OF</span>
                      <span className="text-sm font-bold text-stone-800">{alertaModal.of_numero}</span>
                    </div>
                  )}
                  {(alertaModal.marca || alertaModal.modelo) && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-stone-500">Marca / Modelo</span>
                      <span className="text-sm font-bold text-stone-800">{[alertaModal.marca, alertaModal.modelo].filter(Boolean).join(' / ')}</span>
                    </div>
                  )}
                  {alertaModal.lote_codigo && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-stone-500">Lote</span>
                      <span className="text-sm font-bold text-stone-800">{alertaModal.lote_codigo}</span>
                    </div>
                  )}
                  {alertaModal.operacao_nome && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-stone-500">Operação</span>
                      <span className="text-sm font-bold text-stone-800">{alertaModal.operacao_codigo ? `${alertaModal.operacao_codigo} — ` : ''}{alertaModal.operacao_nome}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            {/* Footer */}
            <div className="px-6 py-4 bg-stone-50 border-t flex gap-3">
              <button
                onClick={() => { setAlertaModal(null); navigate('/gestor/alertas'); }}
                className="flex-1 py-3 px-4 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition-colors text-sm"
                data-testid="alerta-modal-ver-alertas"
              >
                Ver Alertas
              </button>
              <button
                onClick={() => setAlertaModal(null)}
                className="flex-1 py-3 px-4 bg-stone-200 text-stone-700 font-bold rounded-xl hover:bg-stone-300 transition-colors text-sm"
                data-testid="alerta-modal-fechar"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
